/**
 * Color Mixing Simulator Component
 * Real-time color mixing visualization with educational features
 */

import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  Droplets,
  Lightbulb,
  Target,
  RotateCcw,
  Download,
  Info,
  Plus,
  Minus,
} from "lucide-react";
import {
  mixAdditive,
  mixSubtractive,
  type MixingResult,
} from "../../lib/education/mixingUtils";

export type MixingMode =
  | "additive"
  | "subtractive"
  | "comparison"
  | "education";

interface ColorMixingSimulatorProps {
  initialMode?: MixingMode;
  onMixComplete?: (result: MixingResult) => void;
  className?: string;
}

export function ColorMixingSimulator({
  initialMode = "additive",
  onMixComplete,
  className = "",
}: ColorMixingSimulatorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mode, setMode] = useState<MixingMode>(initialMode);
  const [selectedColors, setSelectedColors] = useState<string[]>(["#FF0000"]);
  const [colorRatios, setColorRatios] = useState<number[]>([1]);
  const [mixingResult, setMixingResult] = useState<MixingResult | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  // Available colors for selection
  const availableColors = useMemo(
    () => [
      { name: "Red", hex: "#FF0000" },
      { name: "Green", hex: "#00FF00" },
      { name: "Blue", hex: "#0000FF" },
      { name: "Yellow", hex: "#FFFF00" },
      { name: "Magenta", hex: "#FF00FF" },
      { name: "Cyan", hex: "#00FFFF" },
      { name: "Orange", hex: "#FFA500" },
      { name: "Purple", hex: "#800080" },
      { name: "White", hex: "#FFFFFF" },
      { name: "Black", hex: "#000000" },
    ],
    [],
  );

  // Calculate mixing result
  const calculateMixing = useCallback(() => {
    if (selectedColors.length === 0) return;

    let result: MixingResult;
    if (mode === "additive") {
      result = mixAdditive(selectedColors, colorRatios);
    } else if (mode === "subtractive") {
      result = mixSubtractive(selectedColors, colorRatios);
    } else {
      // Comparison mode - use additive as primary result for display
      result = mixAdditive(selectedColors, colorRatios);
    }

    setMixingResult(result);
    onMixComplete?.(result);
  }, [selectedColors, colorRatios, mode, onMixComplete]);

  // Update mixing when dependencies change
  useEffect(() => {
    calculateMixing();
  }, [calculateMixing]);

  // Draw mixing visualization on canvas
  const drawMixingVisualization = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = "#e5e7eb";
      ctx.lineWidth = 1;
      for (let i = 0; i <= 10; i++) {
        const x = (i * width) / 10;
        const y = (i * height) / 10;

        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    }

    // Draw color mixing visualization
    if (selectedColors.length > 0) {
      const sectionWidth = width / Math.max(selectedColors.length, 1);

      selectedColors.forEach((color, index) => {
        const ratio = colorRatios[index] || 0;
        const actualWidth = sectionWidth * ratio;
        const x = selectedColors
          .slice(0, index)
          .reduce((sum, _, i) => sum + sectionWidth * (colorRatios[i] || 0), 0);

        // Draw color section
        ctx.fillStyle = color;
        ctx.fillRect(x, 0, actualWidth, height * 0.6);

        // Draw label
        if (showLabels) {
          ctx.fillStyle = "#000000";
          ctx.font = "12px sans-serif";
          ctx.textAlign = "center";
          const colorName =
            availableColors.find((c) => c.hex === color)?.name || color;
          ctx.fillText(
            `${colorName} (${Math.round(ratio * 100)}%)`,
            x + actualWidth / 2,
            height * 0.7,
          );
        }
      });

      // Draw mixing result
      if (mixingResult) {
        ctx.fillStyle = mixingResult.resultingColor;
        ctx.fillRect(0, height * 0.8, width, height * 0.2);

        if (showLabels) {
          ctx.fillStyle =
            mixingResult.resultingColor === "#000000" ? "#ffffff" : "#000000";
          ctx.font = "14px sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(
            `Result: ${mixingResult.resultingColor}`,
            width / 2,
            height * 0.9,
          );
        }
      }
    }
  }, [
    selectedColors,
    colorRatios,
    mixingResult,
    showGrid,
    showLabels,
    availableColors,
  ]);

  // Update canvas when dependencies change
  useEffect(() => {
    drawMixingVisualization();
  }, [drawMixingVisualization]);

  // Add color to mixing
  const addColor = useCallback(
    (color: string) => {
      if (selectedColors.length >= 5) return; // Limit to 5 colors

      setSelectedColors((prev) => [...prev, color]);
      setColorRatios((prev) => [...prev, 1]);
    },
    [selectedColors.length],
  );

  // Remove color from mixing
  const removeColor = useCallback((index: number) => {
    if (selectedColors.length <= 1) return; // Keep at least 1 color

    setSelectedColors((prev) => prev.filter((_, i) => i !== index));
    setColorRatios((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Update color ratio
  const updateRatio = useCallback((index: number, ratio: number) => {
    setColorRatios((prev) => {
      const newRatios = [...prev];
      newRatios[index] = Math.max(0.1, Math.min(1, ratio));
      return newRatios;
    });
  }, []);

  // Reset mixing
  const resetMixing = useCallback(() => {
    setSelectedColors(["#FF0000"]);
    setColorRatios([1]);
    setMixingResult(null);
  }, []);

  // Export mixing result
  const exportMixing = useCallback(() => {
    if (!mixingResult) return;

    const exportData = {
      timestamp: new Date().toISOString(),
      mode,
      colors: selectedColors,
      ratios: colorRatios,
      result: mixingResult,
      accuracy: mixingResult.accuracy,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `color-mixing-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [mixingResult, mode, selectedColors, colorRatios]);

  // Animate mixing process
  const animateMixing = useCallback(() => {
    if (selectedColors.length < 2) return;

    setIsAnimating(true);
    let progress = 0;
    const animationDuration = 2000; // 2 seconds
    const startTime = Date.now();
    let animationId: number;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      progress = Math.min(elapsed / animationDuration, 1);

      // Gradually blend colors based on progress
      const animatedRatios = colorRatios.map((ratio, index) => {
        if (index === 0) return ratio;
        return ratio * progress;
      });

      const result =
        mode === "additive"
          ? mixAdditive(selectedColors, animatedRatios)
          : mixSubtractive(selectedColors, animatedRatios);

      setMixingResult(result);

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        calculateMixing(); // Final calculation
      }
    };

    animationId = requestAnimationFrame(animate);

    // Cleanup function
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [selectedColors, colorRatios, mode, calculateMixing]);

  return (
    <div
      className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-6 ${className}`}
    >
      {/* Header */}
      <header>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Color Mixing Simulator
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={animateMixing}
              disabled={isAnimating || selectedColors.length < 2}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
              title="Animate mixing"
            >
              <Lightbulb className="w-4 h-4" />
            </button>
            <button
              onClick={exportMixing}
              disabled={!mixingResult}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
              title="Export mixing result"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mode selector */}
        <div className="flex flex-wrap gap-2">
          {[
            {
              id: "additive" as MixingMode,
              label: "Additive (Light)",
              icon: Lightbulb,
            },
            {
              id: "subtractive" as MixingMode,
              label: "Subtractive (Pigment)",
              icon: Droplets,
            },
            {
              id: "comparison" as MixingMode,
              label: "Comparison",
              icon: Target,
            },
            { id: "education" as MixingMode, label: "Education", icon: Info },
          ].map(({ id, label, icon }) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              disabled={isAnimating}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                mode === id
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              } disabled:opacity-50`}
            >
              {React.createElement(icon, { className: "w-4 h-4" })}
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* Color selection */}
      <div className="space-y-4">
        <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
          Color Selection
        </h4>

        <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
          {availableColors.map((color) => (
            <button
              key={color.hex}
              onClick={() => addColor(color.hex)}
              disabled={
                selectedColors.includes(color.hex) ||
                selectedColors.length >= 5 ||
                isAnimating
              }
              className={`w-12 h-12 rounded-lg border-2 transition-all ${
                selectedColors.includes(color.hex)
                  ? "border-blue-500 ring-2 ring-blue-200"
                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
              } disabled:opacity-50 cursor-pointer`}
              style={{ backgroundColor: color.hex }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      {/* Selected colors and ratios */}
      <div className="space-y-4">
        <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
          Mixing Palette ({selectedColors.length}/5 colors)
        </h4>

        <div className="space-y-3">
          {selectedColors.map((color, index) => (
            <div key={index} className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg border-2 border-gray-300 dark:border-gray-600"
                style={{ backgroundColor: color }}
              />

              <div className="flex-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {availableColors.find((c) => c.hex === color)?.name || color}{" "}
                  Ratio
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      updateRatio(index, (colorRatios[index] || 1) - 0.1)
                    }
                    disabled={isAnimating}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={colorRatios[index] || 1}
                    onChange={(e) =>
                      updateRatio(index, parseFloat(e.target.value))
                    }
                    disabled={isAnimating}
                    className="flex-1"
                  />
                  <button
                    onClick={() =>
                      updateRatio(index, (colorRatios[index] || 1) + 0.1)
                    }
                    disabled={isAnimating}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-12 text-right">
                    {Math.round((colorRatios[index] || 1) * 100)}%
                  </span>
                </div>
              </div>

              <button
                onClick={() => removeColor(index)}
                disabled={selectedColors.length <= 1 || isAnimating}
                className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 disabled:opacity-50"
                title="Remove color"
              >
                <Minus className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Canvas visualization */}
      <div className="space-y-4">
        <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
          Mixing Visualization {isAnimating && "(Animating...)"}
        </h4>

        <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            className="w-full"
            style={{ maxWidth: "600px", height: "auto" }}
          />
        </div>
      </div>

      {/* Mixing result */}
      {mixingResult && (
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
            Mixing Result
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-12 h-12 rounded-lg border-2 border-gray-300 dark:border-gray-600"
                  style={{ backgroundColor: mixingResult.resultingColor }}
                />
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {mixingResult.resultingColor}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {mixingResult.mixingType === "additive"
                      ? "Light Mixing"
                      : "Pigment Mixing"}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                Accuracy
              </div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {mixingResult.accuracy}%
              </div>
            </div>

            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                Components
              </div>
              <div className="text-sm text-green-600 dark:text-green-400">
                {mixingResult.components.length} colors mixed
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {mixingResult.explanation}
            </p>
          </div>
        </div>
      )}

      {/* Display options */}
      <div className="space-y-4">
        <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
          Display Options
        </h4>

        <div className="flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
              disabled={isAnimating}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Show Grid
            </span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
              disabled={isAnimating}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Show Labels
            </span>
          </label>
        </div>

        <button
          onClick={resetMixing}
          disabled={isAnimating}
          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
        >
          <RotateCcw className="w-4 h-4 inline mr-2" />
          Reset Mixing
        </button>
      </div>

      {/* Educational content */}
      {mode === "education" && (
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
            Understanding Color Mixing
          </h4>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="space-y-3">
              <div>
                <h5 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                  Additive Mixing (Light)
                </h5>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Colors add together to become brighter. Red + Green + Blue =
                  White. Used in screens, projectors, and stage lighting.
                </p>
              </div>

              <div>
                <h5 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                  Subtractive Mixing (Pigments)
                </h5>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Colors absorb light and become darker. Red + Yellow + Blue =
                  Dark Brown/Black. Used in painting, printing, and dyes.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-700 dark:text-blue-300">
            <strong>Interactive Tips:</strong> Select up to 5 colors and adjust
            their ratios to see real-time mixing results. Use the animation
            feature to visualize the mixing process. Compare additive (light) vs
            subtractive (pigment) mixing to understand the fundamental
            differences.
          </div>
        </div>
      </div>
    </div>
  );
}
